import React, { useState, useEffect } from 'react';
import { Typography, Input, Button, Select, DatePicker } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useDebounce from '../../hooks/useDebounce';

// Không có plugin này thì dayjs bỏ qua tham số format -> filter dạng "DD/MM" (không có năm) parse sai
dayjs.extend(customParseFormat);

const { Title, Text } = Typography;

// Text-input filter field with debounced onChange (mirrors the search box behaviour)
const DebouncedFilterInput = ({ field, value, onChange }) => {
  const [inputValue, setInputValue] = useState(value ?? '');
  const debounced = useDebounce(inputValue, 500);

  // Sync from external value (URL / back button)
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  // Push debounced changes upward, only when they differ
  useEffect(() => {
    if ((debounced ?? '') !== (value ?? '')) {
      onChange(debounced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <Input
      placeholder={field.label}
      prefix={<SearchOutlined className="text-gray-400" />}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      allowClear
      className="min-w-[150px]"
      style={{ borderRadius: '12px', maxWidth: 200 }}
    />
  );
};

DebouncedFilterInput.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

const PageHeader = ({
  title,
  subtitle,
  onSearch,
  onAdd,
  addButtonLabel = 'Thêm mới',
  showAddButton = true,
  searchPlaceholder = 'Tìm kiếm...',
  searchValue,
  setSearchValue,
  searchFields = [], // Array of {label, value}
  searchField,
  setSearchField,
  filterFields = [], // Array of {label, value, options: [{label, value}]}
  filters, // Object of {field: value}
  setFilters,
  extraActions,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Internal state for the search input
  const [inputValue, setInputValue] = useState(searchValue ?? searchParams.get('search') ?? '');

  useEffect(() => {
    if (searchValue !== undefined && searchValue !== inputValue) {
      setInputValue(searchValue);
    }
  }, [searchValue]);

  const debouncedSearchValue = useDebounce(inputValue, 500);

  // Sync Search with URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentSearch = params.get('search') || '';

    if (debouncedSearchValue !== currentSearch) {
      if (debouncedSearchValue) {
        params.set('search', debouncedSearchValue);
      } else {
        params.delete('search');
      }
      params.set('page', 1);
      setSearchParams(params);
      if (setSearchValue) setSearchValue(debouncedSearchValue);
    }
  }, [debouncedSearchValue, searchParams, setSearchParams, setSearchValue, onSearch]);

  const handleSearchFieldChange = (value) => {
    if (setSearchField) setSearchField(value);
    const params = new URLSearchParams(searchParams);
    if (value) params.set('searchField', value);
    else params.delete('searchField');
    params.set('page', 1);
    setSearchParams(params);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    if (setFilters) setFilters(newFilters);

    const params = new URLSearchParams(searchParams);
    if (value !== undefined && value !== null && value !== '') {
      params.set(field, value);
    } else {
      params.delete(field);
    }
    params.set('page', 1);
    setSearchParams(params);
  };

  const currentSearchField = searchField ?? searchParams.get('searchField') ?? '';

  return (
    <div className="flex flex-col gap-4 mb-6">
      {/* 1. Header Title & Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Title level={3} className="!mb-0 text-gray-800 font-bold">
            {title}
          </Title>
          {subtitle && (
            <Text type="secondary" className="text-gray-500 mt-1 block">
              {subtitle}
            </Text>
          )}
        </div>
        {showAddButton && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAdd}
            className="rounded-xl h-[42px] px-6 bg-blue-600 font-medium shadow-md shadow-blue-100 hidden sm:flex items-center"
          >
            {addButtonLabel}
          </Button>
        )}
      </div>

      {/* 2. Main Search & Filters Row */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
          {/* Search Box with Field Select */}
          <div className="bg-white p-1 rounded-xl shadow-sm border border-gray-100 flex items-center gap-1" style={{ maxWidth: 360, minWidth: 200, flex: '0 1 360px' }}>
            {searchFields.length > 0 && (() => {
              let displaySearchField = currentSearchField;
              if (displaySearchField !== '') {
                const matched = searchFields.find(opt => String(opt.value) === String(displaySearchField));
                if (matched) displaySearchField = matched.value;
              }
              return (
                <Select
                  variant="borderless"
                  className="min-w-[120px] custom-search-select"
                  value={displaySearchField}
                  onChange={handleSearchFieldChange}
                  options={[{ label: 'Tất cả', value: '' }, ...searchFields]}
                />
              );
            })()}
            {searchFields.length > 0 && <div className="w-[1px] h-6 bg-gray-100 mx-1" />}
            <Input
              placeholder={searchPlaceholder}
              prefix={<SearchOutlined className="text-gray-400" />}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              variant="borderless"
              className="flex-1 bg-transparent h-full"
              allowClear
            />
          </div>

          {/* Dynamic Select/Date/Input Filters */}
          {filterFields.map((field) => {
            if (field.type === 'input') {
              const rawVal = filters?.[field.value] ?? searchParams.get(field.value) ?? '';
              return (
                <DebouncedFilterInput
                  key={field.value}
                  field={field}
                  value={rawVal}
                  onChange={(v) => handleFilterChange(field.value, v)}
                />
              );
            }
            if (field.type === 'date') {
              const dateFormat = field.format || 'DD/MM/YYYY';
              const rawVal = filters?.[field.value] ?? searchParams.get(field.value) ?? '';
              const dateValue = rawVal ? dayjs(rawVal, dateFormat) : null;
              return (
                <DatePicker
                  key={field.value}
                  placeholder={field.placeholder || field.label || 'Chọn ngày'}
                  format={dateFormat}
                  value={dateValue && dateValue.isValid() ? dateValue : null}
                  onChange={(date) => handleFilterChange(field.value, date ? date.format(dateFormat) : '')}
                  allowClear
                  style={{ borderRadius: '12px', width: 160 }}
                  variant="filled"
                  {...(field.propsInput || {})}
                />
              );
            }
            const rawVal = filters?.[field.value] ?? searchParams.get(field.value) ?? '';
            let displayValue = rawVal;
            if (rawVal !== '' && field.options) {
              const matched = field.options.find(opt => String(opt.value) === String(rawVal));
              if (matched) displayValue = matched.value;
            }
            return (
              <Select
                key={field.value}
                placeholder={field.label}
                className="min-w-[150px]"
                // antd coi '' là một giá trị thật nên sẽ nuốt placeholder (ô lọc trống trơn,
                // mất tên bộ lọc). Phải trả về undefined khi chưa lọc gì.
                value={rawVal === '' ? undefined : displayValue}
                onChange={(val) => handleFilterChange(field.value, val)}
                options={field.options}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                variant="filled"
                style={{ borderRadius: '12px' }}
              />
            );
          })}

          <div className="flex items-center gap-2">
            {extraActions}
            {showAddButton && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onAdd}
                className="rounded-xl h-[40px] px-6 bg-blue-600 font-medium sm:hidden"
              >
                {addButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  onSearch: PropTypes.func,
  onAdd: PropTypes.func,
  addButtonLabel: PropTypes.string,
  showAddButton: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  searchFields: PropTypes.array,
  searchField: PropTypes.string,
  setSearchField: PropTypes.func,
  filterFields: PropTypes.array,
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  extraActions: PropTypes.node,
};

export default PageHeader;
